import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CarCard from "@/components/CarCard";
import AvailabilitySearch from "@/components/AvailabilitySearch";
import { Seo } from "@/components/Seo";
import {
  CITIES, SERVICE_META, SERVICE_ORDER, hubSlug, childUrl, childLabel,
  serviceIntro, serviceFaqs, serviceMetaTitle, serviceMetaDescription, serviceKeywords,
  cityImage, cityServiceLd,
} from "@/data/cityLocations";
import {
  ChevronDown, ChevronRight, Phone, CheckCircle2, BadgeDollarSign,
} from "lucide-react";

const SITE = "https://belorentals.com";

const FLEET_PREF = {
  economy: ["Economy", "Sedan", "SUV"],
  weekly: ["Sedan", "SUV", "Economy"],
  longterm: ["Sedan", "SUV", "Economy"],
};

export default function CityServicePage({ citySlug, service }) {
  const cfg = CITIES[citySlug];
  const meta = SERVICE_META[service];
  const [cars, setCars] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data || [])).catch(() => {});
  }, []);

  if (!cfg || !meta) return null;

  const path = childUrl(citySlug, service);
  const h1 = `${meta.label} in ${cfg.cityName}, MA`;
  const intro = serviceIntro(citySlug, service);
  const faqs = serviceFaqs(citySlug, service);

  const breadcrumb = [
    { name: "Home", to: "/" },
    { name: `Car Rental in ${cfg.cityName}, MA`, to: `/${hubSlug(citySlug)}` },
    { name: meta.breadcrumb, to: path },
  ];
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumb.map((b, i) => ({
      "@type": "ListItem", position: i + 1, name: b.name, item: `${SITE}${b.to}`,
    })),
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const heroImg = cityImage(citySlug);
  const serviceLd = cityServiceLd(citySlug, h1, meta.label);

  const pref = FLEET_PREF[service] || [];
  const rankCar = (c) => { const i = pref.indexOf(c.category); return i === -1 ? 99 : i; };
  const featured = [...cars].sort((a, b) => rankCar(a) - rankCar(b)).slice(0, 3);

  const siblings = SERVICE_ORDER.filter((s) => s !== service);

  return (
    <div data-testid={`city-service-${citySlug}-${service}`} className="bg-white">
      <Seo
        title={serviceMetaTitle(citySlug, service)}
        description={serviceMetaDescription(citySlug, service)}
        path={path}
        keywords={serviceKeywords(citySlug, service)}
        image={heroImg}
        jsonLd={[breadcrumbLd, faqLd, serviceLd]}
      />

      {/* Hero + breadcrumb */}
      <section className="relative pt-28 pb-14 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Breadcrumb" data-testid="breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
              {breadcrumb.map((b, i) => (
                <li key={b.to} className="flex items-center gap-1.5">
                  {i < breadcrumb.length - 1 ? (
                    <Link to={b.to} className="hover:text-blue-600 transition-colors">{b.name}</Link>
                  ) : (
                    <span className="text-slate-800 font-medium" aria-current="page">{b.name}</span>
                  )}
                  {i < breadcrumb.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-6 text-center">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mb-5">
              <BadgeDollarSign size={15} /> {meta.tagline}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{h1}</h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{intro}</p>
            <div className="mt-9 max-w-2xl mx-auto">
              <AvailabilitySearch />
              <p className="mt-4 text-sm text-slate-500 flex items-center justify-center gap-1.5">
                <Phone size={14} className="text-slate-400" /> Prefer to talk? Call{" "}
                <a href="tel:+19782211451" className="text-blue-600 font-semibold hover:underline">(978) 221-1451</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-2">
        <img
          src={heroImg}
          alt={`${meta.label} in ${cfg.cityName}, MA — Belo Rentals`}
          loading="eager"
          className="w-full aspect-[21/9] object-cover rounded-3xl shadow-card"
          data-testid="city-hero-image"
        />
      </div>

      {/* What this covers */}
      <section className="py-14 px-5 sm:px-8 max-w-3xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900">{meta.label} — how it works</h2>
        <p className="mt-5 text-slate-600 leading-relaxed">{meta.intro}</p>
        <p className="mt-4 text-slate-600 leading-relaxed">{cfg.localIntro}</p>
      </section>

      {/* Use cases */}
      <section className="pb-8 px-5 sm:px-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2.5 mb-5">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-600 text-white"><CheckCircle2 size={18} /></span>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Great for</h2>
        </div>
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="service-use-cases">
          {meta.useCases.map((u, i) => (
            <li key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl border border-slate-100 p-4 text-slate-700 text-sm leading-relaxed">
              <CheckCircle2 size={17} className="text-emerald-500 shrink-0 mt-0.5" /> <span>{u}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Fleet preview */}
      {featured.length > 0 && (
        <section className="py-12 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8 gap-4">
            <h2 className="font-heading text-3xl font-bold text-slate-900">Best-fit cars</h2>
            <Link to="/fleet" className="text-blue-600 font-semibold text-sm hover:underline shrink-0">View all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-3" data-testid="faq-list">
            {faqs.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
          <div className="sr-only">
            {faqs.map((f, i) => (<div key={i}><h3>{f.q}</h3><p>{f.a}</p></div>))}
          </div>
        </div>
      </section>

      {/* Sibling services + back to hub */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">More rental options in {cfg.cityName}</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="related-links">
          <Link to={`/${hubSlug(citySlug)}`} className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
            Car Rental in {cfg.cityName}
          </Link>
          {siblings.map((s) => (
            <Link key={s} to={childUrl(citySlug, s)} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
              {childLabel(citySlug, s)}
            </Link>
          ))}
          {(cfg.nearbyCities || []).filter((n) => CITIES[n]).slice(0, 3).map((n) => (
            <Link key={n} to={`/${hubSlug(n)}`} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
              Car Rental in {CITIES[n].cityName}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Ready to book?</h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">Reserve a clean, well-maintained car in minutes. From $50/day.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/fleet" className="px-7 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">Book your car</Link>
            <Link to="/contact" className="px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
